import { Autocomplete, Box, Button, Grid, Modal, TextField, Tooltip } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import FixtureApi from './api/FixtureApi';
import BaseResponse from '../../app/BaseResponse';
import FixtureModel from './models/FixtureModel';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import Swal from 'sweetalert2';
import FixtureInputModel from './models/FixtureInputModel';
import { FixtureInputSelector, resetFixtureInput, setBizType, setChartererBrokerId, setChartererId, setCollectFrom, setCommRateDetail, setCpDate, setCurrentChartererName, setEstdComm, setInitEvaComplex, setInitEvaVolume, setFixtureInput, setOffice, setOwnerBrokerId, setOwnerId, setPrivateCommRemark, setQuantityOrDuration, setRate, setRef, setRemarks, setSelectedFixtureId, setShowInputPanel, setSupport, setTonnageId, setTonnageVesselName, setTriggerFocusChartererInput, setTriggerSave } from './reducers/FixtureInputReducer';
import { MarketInfoType } from '../MarketInfo/models/MarketInfoQueryModel';
import TonnageNameSelector from '../Tonnage/components/TonnageNameSelector';
import UserSelector from '../UserSelector/UserSelector';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';
import FixtureCommissionDetail from './components/FixtureCommissionDetail/FixtureCommissionDetail';
import CommissionItemModel from './components/FixtureCommissionDetail/models/CommissionItemModel';
import TonnageNameModel from '../Tonnage/models/TonnageNameModel';
import CompanyNamePICSelector from '../CompanyNamePICSelector/CompanyNamePICSelector';
import CompanyNamePICModel from '../CompanyNamePIC/models/CompanyNamePICModel';
import { setChartererName } from '../MarketInfo/reducers/MarketInfoInputReducer';

const FixtureInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const fixtureInputViewData = useSelector(FixtureInputSelector);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "85%",
        bgcolor: 'white',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        maxHeight: "calc(100% - 100px)",
        overflow: "auto"
    };

    useEffect(() => {
        if (fixtureInputViewData.triggerSave > 0) {
            save();
            dispatch(setTriggerSave(0));
        }
    }, [fixtureInputViewData.triggerSave]);

    useEffect(() => {
        if (fixtureInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [fixtureInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetFixtureInput());
        dispatch(setSelectedFixtureId(""));
        dispatch(setTriggerFocusChartererInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (fixtureInputViewData.selectedFixtureId.length > 0) {
            selectFixtureById(fixtureInputViewData.selectedFixtureId);
        }
    }, [fixtureInputViewData.selectedFixtureId])

    const getEmailSubject = () => {
        return `Clean recap of MV ${fixtureInputViewData.input.tonnageVesselName ? fixtureInputViewData.input.tonnageVesselName : ""} / ${fixtureInputViewData.currentChartererName} - C/P dated ${fixtureInputViewData.input.cpDate ? Utility.DateFormat.formatDateString(fixtureInputViewData.input.cpDate) : ""}`
    }

    const selectFixtureById = (id: string) => {
        dispatch(showLoading(true));
        FixtureApi.GetById({
            id: id
        }).then((response: BaseResponse<FixtureModel>) => {
            if (response.success) {
                let fixtureInput = Utility.CloneObject(response.data) as FixtureInputModel;

                fixtureInput.cpDate = Utility.DateFormat.format(new Date(response.data.cpDate));

                if (response.message.length > 0) {
                    Utility.Alert.Error(response.message);
                }

                dispatch(setFixtureInput(fixtureInput));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const save = () => {
        try {
            validate();

            dispatch(showLoading(true));

            FixtureApi.Save(fixtureInputViewData.input)
                .then((response: BaseResponse<FixtureModel>) => {
                    if (response.success) {
                        if (fixtureInputViewData.input.id.length === 0) {
                            Utility.Alert.Success("Fixture added successful");
                        } else {
                            Utility.Alert.Success("Fixture updated successful");
                        }

                        dispatch(showLoading(false));

                        refreshInput();

                        dispatch(setShowInputPanel(false));
                    } else {
                        if (response.message === "Need Confirm") {
                            Swal.fire({
                                title: 'The order you are trying to input may be existed. Do you wish to add anyway?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#3085d6',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'Yes',
                                cancelButtonText: 'No'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    FixtureApi.Save(fixtureInputViewData.input)
                                        .then((response: BaseResponse<FixtureModel>) => {
                                            if (response.success) {
                                                if (fixtureInputViewData.input.id.length === 0) {
                                                    Utility.Alert.Success("Fixture added successful");
                                                } else {
                                                    Utility.Alert.Success("Fixture updated successful");
                                                }

                                                dispatch(showLoading(false));

                                                refreshInput();
                                            }
                                        });
                                }
                            })
                        } else {
                            dispatch(showLoading(false));
                            Utility.Alert.Error(response.message);
                        }
                    }
                })
                .catch((error) => {
                    dispatch(showLoading(false));
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            dispatch(showLoading(false));
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (fixtureInputViewData.input.office.length === 0) {
            throw "Office is required";
        }

        if (fixtureInputViewData.input.bizType.length === 0) {
            throw "Biz Type is required";
        }

        if (fixtureInputViewData.input.chartererId.length === 0) {
            throw "Ref is required";
        }

        if (fixtureInputViewData.input.tonnageId.length === 0) {
            throw "Tonnage is required";
        }

        if (fixtureInputViewData.input.ownerId.length === 0) {
            throw "Owner is required";
        }

        if (!fixtureInputViewData.input.ownerBrokerId || fixtureInputViewData.input.ownerBrokerId === 0) {
            throw "Owner broker is required";
        }

        if (fixtureInputViewData.input.chartererId.length === 0) {
            throw "Charterer is required";
        }

        if (!fixtureInputViewData.input.chartererBrokerId || fixtureInputViewData.input.chartererBrokerId === 0) {
            throw "Charterer broker is required";
        }

        if (fixtureInputViewData.input.quantityOrDuration === 0) {
            throw "Quantity or duration is required";
        }

        if (fixtureInputViewData.input.rate === 0) {
            throw "Rate is required";
        }

        if (fixtureInputViewData.input.cpDate.length === 0) {
            throw "Cp date is required";
        }

        if (fixtureInputViewData.input.collectFrom.length === 0) {
            throw "Collect from is required";
        }

        if (fixtureInputViewData.input.initEvaVolume === 0) {
            throw "Eva Volume is required";
        }

        if (fixtureInputViewData.input.initEvaComplex === 0) {
            throw "Eva Complex is required";
        }

        if (fixtureInputViewData.input.estdComm === 0) {
            throw "Estd Comm is required";
        }

        if (fixtureInputViewData.input.privateCommRemark.length === 0) {
            throw "Co-worker Comm is required";
        }

        if (fixtureInputViewData.input.remarks.length === 0) {
            throw "Remarks is required";
        }

        let commissions = JSON.parse(fixtureInputViewData.input.commRateDetail) as CommissionItemModel[];
        let commissionCount = 0;
        if (commissions.length === 0) {
            throw "Commissions is required";
        } else {
            _.each(commissions, (commission) => {
                if (!commission.brokerId) {
                    throw "Please select broker for commission";
                }

                if (commission.brokerId) {
                    commissionCount += commission.percent;
                }
            });

            if (commissionCount < 100) {
                throw "Sum of all commissions must be 100";
            }
        }
    }

    return (
        <>
            {
                (!fixtureInputViewData.isEdit || fixtureInputViewData.input.tonnageId.length > 0) &&
                <Modal
                    open={fixtureInputViewData.showInputPanel}
                    onClose={() => {
                        dispatch(setShowInputPanel(false));
                    }}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box sx={style}>
                        <Grid container>
                            <Grid item lg={3} xs={12}>
                                <Grid container className='Fixture-input-component'>
                                    <Grid xs={10} lg={9}>
                                        <UserSelector
                                            label='Supporter'
                                            className='cargo-input-component'
                                            selectedUserId={fixtureInputViewData.input.support}
                                            onUserIdChange={(value: number) => {
                                                dispatch(setSupport(value));
                                            }}
                                        >
                                        </UserSelector>
                                    </Grid>
                                    <Grid item xs={2} lg={3}>
                                        <Tooltip title="Reset (Alt + R)">
                                            <Button
                                                className="clear-input-button"
                                                color="success"
                                                variant="contained"
                                                tabIndex={-1}
                                                onClick={() => {
                                                    refreshInput();
                                                }}
                                            >
                                                <i className="fa fa-refresh" aria-hidden="true"></i>
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                {
                                    fixtureInputViewData.isEdit &&
                                    // <h3><b>Ref: {fixtureInputViewData.input.ref}</b></h3>
                                    <TextField
                                        fullWidth
                                        className="cargo-input-component"
                                        label="Ref"
                                        value={fixtureInputViewData.input.ref}
                                        onChange={(event) => {
                                            dispatch(setRef(event.target.value));
                                        }}
                                    >
                                    </TextField>
                                }

                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        save();
                                    }}
                                >Save</Button>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                {
                                    fixtureInputViewData.isEdit &&
                                    <h3><b>Created By: {fixtureInputViewData.input.createdByUserName}</b></h3>
                                }
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={3} xs={12}>
                                <Autocomplete
                                    className='cargo-input-component'
                                    disableClearable={true}
                                    options={["Haiphong", "Seoul", "Singapore"]}
                                    renderInput={(params) => <TextField {...params} label="Office" />}
                                    value={fixtureInputViewData.input.office}
                                    onChange={(event: React.SyntheticEvent, value: string) => {
                                        dispatch(setOffice(value));
                                    }}
                                />
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <Autocomplete
                                    className='cargo-input-component'
                                    disableClearable={true}
                                    options={[MarketInfoType.Voy, MarketInfoType.TCT, MarketInfoType.TC, MarketInfoType.COA]}
                                    renderInput={(params) => <TextField {...params} label="Biz Type" />}
                                    value={fixtureInputViewData.input.bizType}
                                    onChange={(event: React.SyntheticEvent, value: string) => {
                                        dispatch(setBizType(value));
                                    }}
                                />
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <StartEndDatepicker
                                    startDateLabel="Cp Date"
                                    className="cargo-input-component"
                                    useSingleMode={true}
                                    useQuickEndDateInput={false}
                                    onStartDateChange={(date: string) => {
                                        dispatch(setCpDate(date));
                                    }}
                                    startDateValue={fixtureInputViewData.input.cpDate.toUpperCase()}
                                    allowFreeInput={true}
                                    disableAutoYear={true}
                                >
                                </StartEndDatepicker>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <TonnageNameSelector
                                    className='cargo-input-component'
                                    triggerFocusInput={0}
                                    selectedTonnageId={fixtureInputViewData.input.tonnageId}
                                    currentVesselName={fixtureInputViewData.input.tonnageVesselName}
                                    onTonnageIdChange={(value: string, selectedItem: TonnageNameModel) => {
                                        dispatch(setTonnageId(value));
                                        dispatch(setTonnageVesselName(selectedItem.vesselName));
                                    }}
                                    onVesselNameChange={(value: string) => {
                                        dispatch(setTonnageVesselName(value));
                                    }}
                                >
                                </TonnageNameSelector>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={3} xs={12}>
                                <CompanyNamePICSelector
                                    label='Owner'
                                    className='cargo-input-component'
                                    selectedId={fixtureInputViewData.input.ownerId}
                                    onChange={(selectedItem: CompanyNamePICModel | null) => {
                                        dispatch(setOwnerId(selectedItem ? selectedItem.id : ""));
                                    }}
                                >
                                </CompanyNamePICSelector>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <UserSelector
                                    label='Owner Broker'
                                    className='cargo-input-component'
                                    selectedUserId={fixtureInputViewData.input.ownerBrokerId}
                                    onUserIdChange={(value: number) => {
                                        dispatch(setOwnerBrokerId(value));
                                    }}
                                >
                                </UserSelector>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <CompanyNamePICSelector
                                    label='Charterer'
                                    className='cargo-input-component'
                                    selectedId={fixtureInputViewData.input.chartererId}
                                    onChange={(selectedItem: CompanyNamePICModel | null) => {
                                        dispatch(setCurrentChartererName(selectedItem ? selectedItem.companyName : ""))
                                        dispatch(setChartererId(selectedItem ? selectedItem.id : ""));
                                    }}
                                >
                                </CompanyNamePICSelector>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <UserSelector
                                    label='Charterer Broker'
                                    className='cargo-input-component'
                                    selectedUserId={fixtureInputViewData.input.chartererBrokerId}
                                    onUserIdChange={(value: number) => {
                                        dispatch(setChartererBrokerId(value));
                                    }}
                                >
                                </UserSelector>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={3} xs={12}>
                                <TextField
                                    fullWidth
                                    className="cargo-input-component"
                                    label={fixtureInputViewData.input.bizType == MarketInfoType.Voy ? "Quantity" : "Duration"}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom as any,
                                    }}
                                    value={fixtureInputViewData.input.quantityOrDuration ? fixtureInputViewData.input.quantityOrDuration : ""}
                                    onChange={(event) => {
                                        let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                        dispatch(setQuantityOrDuration(value));
                                    }}
                                >
                                </TextField>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <TextField
                                    fullWidth
                                    className="cargo-input-component"
                                    label="Rate"
                                    InputProps={{
                                        inputComponent: NumberFormatCustom as any,
                                    }}
                                    value={fixtureInputViewData.input.rate ? fixtureInputViewData.input.rate : ""}
                                    onChange={(event) => {
                                        let rate = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                        dispatch(setRate(rate));

                                        if (rate > 0 || fixtureInputViewData.input.quantityOrDuration > 0) {
                                            let estdComm = rate * fixtureInputViewData.input.quantityOrDuration * 1.25 / 100;
                                            dispatch(setEstdComm(estdComm));
                                        }
                                    }}
                                >
                                </TextField>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <TextField
                                    fullWidth
                                    className="cargo-input-component"
                                    label="Eva Volume"
                                    InputProps={{
                                        inputComponent: NumberFormatCustom as any,
                                    }}
                                    value={fixtureInputViewData.input.initEvaVolume ? fixtureInputViewData.input.initEvaVolume : ""}
                                    onChange={(event) => {
                                        let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                        dispatch(setInitEvaVolume(value));
                                    }}
                                >
                                </TextField>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <TextField
                                    fullWidth
                                    className="cargo-input-component"
                                    label="Eva Complex"
                                    InputProps={{
                                        inputComponent: NumberFormatCustom as any,
                                    }}
                                    value={fixtureInputViewData.input.initEvaComplex ? fixtureInputViewData.input.initEvaComplex : ""}
                                    onChange={(event) => {
                                        let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                        dispatch(setInitEvaComplex(value));
                                    }}
                                >
                                </TextField>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={3} xs={12}>
                                <CompanyNamePICSelector
                                    label='Collect From'
                                    className='cargo-input-component'
                                    selectedId={fixtureInputViewData.input.collectFrom}
                                    onChange={(selectedItem: CompanyNamePICModel | null) => {
                                        dispatch(setCollectFrom(selectedItem ? selectedItem.id : ""));
                                    }}
                                >
                                </CompanyNamePICSelector>
                            </Grid>
                            <Grid item lg={3} xs={12}>
                                <TextField
                                    label="Estd Comm"
                                    fullWidth
                                    value={fixtureInputViewData.input.estdComm ? fixtureInputViewData.input.estdComm.toFixed(2) : ""}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom as any,
                                    }}
                                    onChange={(event) => {
                                        dispatch(setEstdComm(parseFloat(event.target.value)));
                                    }}
                                    className='cargo-input-component'
                                />
                            </Grid>
                            <Grid item lg={6} xs={12}>
                                <span className='fixture-estd-comm-note'>
                                    *Pre-input 1.25% by default. Brokers edit if it is different structure.
                                </span>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} xs={12}>
                                <TextField
                                    label="Co-broker comm"
                                    multiline
                                    fullWidth
                                    rows={3}
                                    value={fixtureInputViewData.input.privateCommRemark}
                                    onChange={(event) => {
                                        dispatch(setPrivateCommRemark(event.target.value));
                                    }}
                                    className="tonnage-input-full-tc cargo-input-component"
                                />
                            </Grid>
                            <Grid item lg={6} xs={12}>
                                <TextField
                                    label="Remarks"
                                    multiline
                                    fullWidth
                                    rows={3}
                                    value={fixtureInputViewData.input.remarks}
                                    onChange={(event) => {
                                        dispatch(setRemarks(event.target.value));
                                    }}
                                    className="tonnage-input-full-tc cargo-input-component"
                                />
                            </Grid>
                        </Grid>
                        <div>
                            <FixtureCommissionDetail
                                selectedItems={JSON.parse(fixtureInputViewData.input.commRateDetail) as CommissionItemModel[]}
                                onItemChange={(selectedItem: CommissionItemModel[]) => {
                                    dispatch(setCommRateDetail(JSON.stringify(selectedItem)));
                                }}
                            >
                            </FixtureCommissionDetail>
                        </div>
                        <div>
                            Please forward clean recap email to support@ascentbulk.com with email subject "<span id='fixture-email-subject'>{getEmailSubject()}</span>". Thank you.
                        </div>
                        <Button
                            color="success"
                            variant='outlined'
                            onClick={() => {
                                navigator.clipboard.writeText(getEmailSubject());
                                Utility.Alert.Success("Copied to clipboard");
                            }}>
                            Copy email subject
                        </Button>
                    </Box>
                </Modal>
            }
        </>
    )
};

export default FixtureInput;